import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Collapse,
  Heading,
  HeadingProps,
  Skeleton,
  forwardRef,
} from "@chakra-ui/react";
import { useBrandQuery } from "utils/apollo/hooks/brand";
import { makeFormatCurrency } from "utils/formatting";
import { selectedSku } from "utils/links";
import Link from "components/Shared/Link";
import {
  PricingCalculatorInput,
  PricingCalculatorOutput,
  PricingCalculatorSectionContainer,
} from "./PricingCalculatorSection";
import { skuPricingCalculatorQuery } from "./pricingCalculatorUtils";

type LabeledHeadingProps = HeadingProps & {
  label: React.ReactNode;
};

const LabeledHeading = forwardRef<LabeledHeadingProps, typeof Heading>(
  ({ label, children, ...rest }, ref) => (
    <Heading ref={ref} {...rest}>
      <Box as="span" fontWeight={300} fontSize=".9em">
        {label}:{" "}
      </Box>
      {children}
    </Heading>
  ),
);

type Props = {
  skuId: UUID;
};

export default function SelectedSkuSection({ skuId }: Props) {
  const { data, loading } = useBrandQuery(skuPricingCalculatorQuery, {
    variables: { skuId },
  });

  return (
    <PricingCalculatorSectionContainer mt={12}>
      <PricingCalculatorInput>
        <Skeleton
          isLoaded={!loading}
          aria-busy={loading}
          _loading={{ maxWidth: "10em" }}
        >
          <LabeledHeading
            as="h3"
            size="sm"
            color="magenta.dark"
            label="Product Line"
          >
            {data?.sku?.productLine?.name || " "}
          </LabeledHeading>
        </Skeleton>

        <Skeleton
          isLoaded={!loading}
          aria-busy={loading}
          _loading={{ maxWidth: "9em" }}
        >
          <LabeledHeading
            as="h4"
            size="xs"
            color="magenta.veryDark"
            label="SKU"
            mt={2}
            mb={1}
          >
            {data?.sku?.name || " "}
          </LabeledHeading>
        </Skeleton>

        <Link
          href={selectedSku(data?.sku?.id)}
          color="lightPink"
          fontSize="xs"
          _hover={{ color: "magenta.dark" }}
        >
          Product Information{" "}
          <Box as="span" fontSize=".9em">
            ▶
          </Box>
        </Link>

        <Collapse in={data?.sku?.unitCogs === null}>
          <Alert status="warning" mt={6}>
            <AlertIcon />
            <AlertDescription>
              No <b>Unit COGS</b> has been set for the <b>{data?.sku?.name}</b>{" "}
              SKU. Please enter a value in Product Info to use the Pricing
              Calculator.
            </AlertDescription>
          </Alert>
        </Collapse>
      </PricingCalculatorInput>

      <PricingCalculatorOutput
        statLabel="Unit COGS"
        statNumber={makeFormatCurrency({ minimumFractionDigits: 2 })(
          data?.sku?.unitCogs,
        )}
        isStatNumberLoading={loading}
      />
    </PricingCalculatorSectionContainer>
  );
}
